import React, { useState } from 'react';
import { US_STATES, PlusIconPath } from './constants';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';

interface Props {
  data: any;
  onChange: (data: any) => void;
}

const TrashIcon: React.FC<{ className?: string }> = ({ className = 'w-4 h-4' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
  </svg>
);

const MapPinIcon: React.FC<{ className?: string }> = ({ className = 'w-10 h-10' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z" />
  </svg>
);

const AreasEditor: React.FC<Props> = ({ data, onChange }) => {
  const form = data || { areas: [], primaryServiceArea: '' };
  const [expandedIdx, setExpandedIdx] = useState<number | null>(null);

  const emit = (newForm: any) => onChange(newForm);

  const parseRadius = (val: any): number => {
    if (typeof val === 'number') return val;
    const n = parseInt(String(val), 10);
    return isNaN(n) ? 25 : n;
  };

  const updateArea = (idx: number, field: string, value: any) => {
    const areas = [...form.areas];
    areas[idx] = { ...areas[idx], [field]: value };
    emit({ ...form, areas });
  };

  const updateArrayField = (areaIdx: number, field: string, itemIdx: number, value: string) => {
    const areas = [...form.areas];
    const arr = [...(areas[areaIdx][field] || [])];
    arr[itemIdx] = value;
    areas[areaIdx] = { ...areas[areaIdx], [field]: arr };
    emit({ ...form, areas });
  };

  const addArrayItem = (areaIdx: number, field: string) => {
    const areas = [...form.areas];
    areas[areaIdx] = { ...areas[areaIdx], [field]: [...(areas[areaIdx][field] || []), ''] };
    emit({ ...form, areas });
  };

  const removeArrayItem = (areaIdx: number, field: string, itemIdx: number) => {
    const areas = [...form.areas];
    const arr = [...(areas[areaIdx][field] || [])];
    arr.splice(itemIdx, 1);
    areas[areaIdx] = { ...areas[areaIdx], [field]: arr };
    emit({ ...form, areas });
  };

  const addArea = () => {
    const areas = [...(form.areas || []), {
      slug: 'new-area',
      name: 'New Area',
      state: '',
      description: '',
      neighborhoods: [],
      landmarks: [],
    }];
    emit({ ...form, areas });
    setExpandedIdx(areas.length - 1);
  };

  const removeArea = (idx: number) => {
    const areas = [...form.areas];
    areas.splice(idx, 1);
    emit({ ...form, areas });
    if (expandedIdx === idx) setExpandedIdx(null);
    else if (expandedIdx !== null && expandedIdx > idx) setExpandedIdx(expandedIdx - 1);
  };

  const radiusNum = parseRadius(form.serviceRadius);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="text-lg font-display font-semibold text-primary">Service Areas</h2>
          <p className="text-sm text-muted mt-1">Geographic coverage and service zones</p>
        </div>
        <button onClick={addArea} className="inline-flex items-center gap-2 px-4 py-2 bg-accent/10 text-accent rounded-lg text-sm font-medium hover:bg-accent/20 transition-colors">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d={PlusIconPath} />
          </svg>
          Add Area
        </button>
      </div>

      <Card>
        <CardHeader><CardTitle>Coverage</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label>Main city or area you serve</Label>
            <Input value={form.primaryServiceArea || ''} onChange={(e) => emit({ ...form, primaryServiceArea: e.target.value })} placeholder="e.g. Phoenix, AZ" />
            <p className="text-xs text-muted">Your primary service location shown in headlines</p>
          </div>
          <div className="space-y-1.5">
            <Label>
              Service Radius — <span className="font-semibold text-accent">{radiusNum} miles</span>
            </Label>
            <input
              type="range"
              min={5}
              max={100}
              step={5}
              value={radiusNum}
              onChange={(e) => emit({ ...form, serviceRadius: `${e.target.value} miles` })}
              className="w-full accent-accent h-2 rounded-lg cursor-pointer mt-2"
            />
            <div className="flex justify-between text-xs text-muted mt-1">
              <span>5 mi</span>
              <span>100 mi</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {(!form.areas || form.areas.length === 0) && (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <MapPinIcon className="w-10 h-10 text-zinc-300 mb-3" />
          <p className="text-sm text-muted mb-4">No service areas yet</p>
          <button onClick={addArea} className="px-4 py-2 bg-accent text-white rounded-lg text-sm font-medium hover:bg-accent/90 transition-colors">
            Add your first area
          </button>
        </div>
      )}

      {(form.areas || []).map((area: any, idx: number) => (
        <div key={idx} className="border border-zinc-200 rounded-lg overflow-hidden bg-white">
          <button
            onClick={() => setExpandedIdx(expandedIdx === idx ? null : idx)}
            className="w-full px-4 py-3 flex items-center justify-between bg-zinc-50 hover:bg-zinc-100 transition-colors text-left"
          >
            <span className="font-medium text-primary">{area.name || `Area ${idx + 1}`}</span>
            <div className="flex items-center gap-2">
              <span
                role="button"
                onClick={(e) => { e.stopPropagation(); removeArea(idx); }}
                className="p-1 text-zinc-400 hover:text-red-500 transition-colors"
                title="Remove area"
              >
                <TrashIcon />
              </span>
              <svg className={`w-4 h-4 text-muted transition-transform ${expandedIdx === idx ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
              </svg>
            </div>
          </button>

          {expandedIdx === idx && (
            <div className="p-4 space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-1.5">
                  <Label>City / Area Name</Label>
                  <Input value={area.name || ''} onChange={(e) => updateArea(idx, 'name', e.target.value)} placeholder="e.g. Scottsdale" />
                </div>
                <div className="space-y-1.5">
                  <Label>State</Label>
                  <Select value={area.state || undefined} onValueChange={(v) => updateArea(idx, 'state', v)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select..." />
                    </SelectTrigger>
                    <SelectContent>
                      {US_STATES.map((st) => (
                        <SelectItem key={st} value={st}>{st}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Approximate population</Label>
                  <Input type="number" value={area.population || ''} onChange={(e) => updateArea(idx, 'population', e.target.value ? Number(e.target.value) : undefined)} placeholder="e.g. 250000" />
                  <p className="text-xs text-muted">Optional — used for SEO copy</p>
                </div>
              </div>

              <div className="space-y-1.5">
                <Label>Area description</Label>
                <textarea rows={3} value={area.description || ''} onChange={(e) => updateArea(idx, 'description', e.target.value)}
                  placeholder="A couple of sentences about this area — what you do there, why customers trust you, etc."
                  className="flex min-h-[80px] w-full rounded-md border border-zinc-200 bg-zinc-100/70 px-3 py-2 text-sm text-zinc-900 placeholder:text-zinc-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/30 focus-visible:border-accent transition-colors" />
              </div>

              {(['neighborhoods', 'landmarks', 'serviceHighlights'] as const).map((field) => {
                const fieldLabels: Record<string, { label: string; hint: string }> = {
                  neighborhoods:    { label: 'Neighborhoods', hint: 'Neighborhood names within this area you serve — e.g. "Old Town", "Arcadia"' },
                  landmarks:        { label: 'Nearby Landmarks', hint: 'Known landmarks nearby — helps with SEO — e.g. "Desert Ridge Marketplace"' },
                  serviceHighlights: { label: 'Service Highlights', hint: 'Why you\'re great in this area — e.g. "Same-day service available"' },
                };
                const { label: fLabel, hint: fHint } = fieldLabels[field] ?? { label: field, hint: '' };
                return (
                <div key={field}>
                  <Label className="mb-0.5 block">{fLabel}</Label>
                  {fHint && <p className="text-xs text-muted mb-1.5">{fHint}</p>}
                  {(area[field] || []).map((item: string, ii: number) => (
                    <div key={ii} className="flex gap-2 mb-2">
                      <Input value={item} onChange={(e) => updateArrayField(idx, field, ii, e.target.value)} className="flex-1" />
                      <button onClick={() => removeArrayItem(idx, field, ii)} className="px-2 text-red-500 hover:text-red-700">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" /></svg>
                      </button>
                    </div>
                  ))}
                  <button onClick={() => addArrayItem(idx, field)} className="inline-flex items-center gap-1 text-sm text-accent hover:text-accent/80">
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d={PlusIconPath} />
                    </svg>
                    Add
                  </button>
                </div>
                );
              })}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default AreasEditor;
